import { useEffect, useRef } from "@wordpress/element";
import {
    Settings,
    ZoomIn,
    Monitor,
    Images,
    Maximize2,
    Smartphone,
    Tag,
    Play,
    SlidersHorizontal,
    LayoutGrid,
    Image,
    Navigation2,
    Wand2,
    Layers,
    LayoutTemplate,
    X,
} from "lucide-react";
import { useSettings } from "../context/SettingsContext";

const SECTIONS = [
    {
        label: null,
        tabs: [
            { id: "general",     label: "General",     icon: Settings      },
            { id: "quick-start", label: "Quick Start", icon: LayoutTemplate },
        ],
    },
    {
        label: "Zoom",
        tabs: [
            { id: "zoom",        label: "Zoom",        icon: ZoomIn  },
            { id: "zoom-window", label: "Zoom Window", icon: Monitor },
        ],
    },
    {
        label: "Display",
        tabs: [
            { id: "gallery",  label: "Gallery",  icon: Images    },
            { id: "lightbox", label: "Lightbox", icon: Maximize2 },
            { id: "mobile",   label: "Mobile",   icon: Smartphone },
            { id: "video",    label: "Video",    icon: Play      },
        ],
    },
];

const GALLERY_SUB_TABS = [
    { id: "general",    label: "General",        icon: SlidersHorizontal },
    { id: "thumbnails", label: "Thumbnails",     icon: LayoutGrid, hideForHierarchyGrid: true, hideForTiles: true },
    { id: "main-image", label: "Main Image",     icon: Image, hideForTiles: true },
    { id: "navigation", label: "Navigation",     icon: Navigation2, hideForHierarchyGrid: true, hideForTiles: true },
    { id: "transition", label: "Transition",     icon: Wand2, hideForAnchor: true, hideForStickySplit: true, hideForHierarchyGrid: true, hideForTiles: true },
    { id: "stacked",        label: "Stacked Layout",   icon: Layers, requireAnchor: true },
    { id: "sticky-split",   label: "Sticky Split",     icon: Layers, requireStickySplit: true },
    { id: "hierarchy-grid", label: "Hierarchy Grid",   icon: Layers, requireHierarchyGrid: true },
    { id: "tiles",          label: "Tiles",            icon: Layers, requireTiles: true },
];

function NavButton({ tab, active, onClick, children }) {
    const { icon: Icon, label } = tab;
    return (
        <button
            type="button"
            onClick={onClick}
            className={[
                "group flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-start",
                "text-[13px] font-medium transition-colors duration-100",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset",
                active
                    ? "bg-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
            ].join(" ")}
        >
            <Icon
                className={[
                    "h-[15px] w-[15px] shrink-0 transition-colors",
                    active ? "text-blue-100" : "text-gray-400 group-hover:text-gray-500",
                ].join(" ")}
                strokeWidth={2}
            />
            <span className="truncate">{label}</span>
            {children}
        </button>
    );
}

export default function SettingsSidebar({ isOpen, onClose }) {
    const {
        state: { activeTab, gallerySubTab, settings },
        actions,
    } = useSettings();

    const isAnchorGallery        = settings.galleryType === "anchor";
    const isStickySplitGallery   = settings.galleryType === "sticky-split";
    const isHierarchyGridGallery = settings.galleryType === "hierarchy-grid";
    const isTilesGallery         = settings.galleryType === "tiles";

    // Mirror #adminmenuback height — WP keeps this element exactly as tall as
    // the admin menu area, so it's the most reliable source for our sidebar height.
    const sidebarRef = useRef(null);
    useEffect(() => {
        const update = () => {
            const menuBack  = document.getElementById("adminmenuback");
            const adminBar  = document.getElementById("wpadminbar");
            if (!menuBack || !sidebarRef.current) return;
            const barH = adminBar ? adminBar.offsetHeight : 0;
            sidebarRef.current.style.height = window.innerWidth > 782
                ? `${menuBack.offsetHeight + barH}px`
                : "calc(100vh - var(--wp-admin--admin-bar--height, 32px))";
        };
        update();
        window.addEventListener("resize", update);
        document.addEventListener("wp-collapse-menu", update);
        document.addEventListener("wp-responsive-activate", update);
        document.addEventListener("wp-responsive-deactivate", update);
        return () => {
            window.removeEventListener("resize", update);
            document.removeEventListener("wp-collapse-menu", update);
            document.removeEventListener("wp-responsive-activate", update);
            document.removeEventListener("wp-responsive-deactivate", update);
        };
    }, []);

    const adminBarHeight = "var(--wp-admin--admin-bar--height, 32px)";

    return (
        <aside
            ref={sidebarRef}
            className={[
                "fixed start-0 bottom-0 z-50 flex w-64 shrink-0 flex-col border-e border-gray-200/80 bg-white",
                "transition-transform duration-300 ease-in-out",
                "wp:sticky wp:bottom-auto wp:z-auto wp:self-start wp:translate-x-0",
                isOpen ? "translate-x-0" : "-translate-x-full",
            ].join(" ")}
            style={{ top: adminBarHeight }}
        >
            {/* Brand header — height matches the content header (h-[68px]) */}
            <div className="flex h-[68px] shrink-0 items-center gap-3 border-b border-gray-200/80 px-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 shadow-sm shadow-blue-200/60">
                    <ZoomIn className="h-[15px] w-[15px] text-white" strokeWidth={2.5} />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold leading-tight text-gray-900">
                        Image Zoom
                    </p>
                    <p className="text-[11px] leading-tight text-gray-400">
                        Rule Settings
                    </p>
                </div>
                {/* Close button — mobile only */}
                <button
                    type="button"
                    onClick={onClose}
                    className="flex wp:hidden h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Close navigation"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto px-3 py-3">

                {/* Main sections */}
                {SECTIONS.map(({ label, tabs }, sectionIdx) => (
                    <div key={sectionIdx} className={sectionIdx > 0 ? "mt-4" : ""}>
                        {label && (
                            <p className="mb-1 px-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-gray-400">
                                {label}
                            </p>
                        )}
                        <div className="space-y-0.5">
                            {tabs.map((tab) => {
                                const active      = activeTab === tab.id;
                                const isGallery   = tab.id === "gallery";
                                const showSubTabs = isGallery && active;

                                return (
                                    <div key={tab.id}>
                                        <NavButton
                                            tab={tab}
                                            active={active}
                                            onClick={() => { actions.setTab(tab.id); onClose(); }}
                                        />

                                        {/* Gallery sub-tabs */}
                                        {showSubTabs && (
                                            <div className="ms-3 mt-0.5 space-y-0.5 border-s-2 border-blue-100 ps-2">
                                                {GALLERY_SUB_TABS.filter(
                                                    (t) =>
                                                        (!t.requireAnchor        || isAnchorGallery)        &&
                                                        (!t.hideForAnchor        || !isAnchorGallery)       &&
                                                        (!t.requireStickySplit   || isStickySplitGallery)   &&
                                                        (!t.hideForStickySplit   || !isStickySplitGallery)  &&
                                                        (!t.requireHierarchyGrid || isHierarchyGridGallery) &&
                                                        (!t.hideForHierarchyGrid || !isHierarchyGridGallery) &&
                                                        (!t.requireTiles         || isTilesGallery)         &&
                                                        (!t.hideForTiles         || !isTilesGallery),
                                                ).map(({ id: subId, label: subLabel, icon: SubIcon }) => {
                                                    const subActive = gallerySubTab === subId;
                                                    return (
                                                        <button
                                                            key={subId}
                                                            type="button"
                                                            onClick={() => { actions.setGallerySubTab(subId); onClose(); }}
                                                            className={[
                                                                "group flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-start",
                                                                "text-[12px] font-medium transition-colors duration-100",
                                                                "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset",
                                                                subActive
                                                                    ? "bg-blue-50 text-blue-700"
                                                                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-800",
                                                            ].join(" ")}
                                                        >
                                                            <SubIcon
                                                                className={[
                                                                    "h-3 w-3 shrink-0",
                                                                    subActive ? "text-blue-500" : "text-gray-400 group-hover:text-gray-500",
                                                                ].join(" ")}
                                                                strokeWidth={2}
                                                            />
                                                            {subLabel}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

                {/* Assignments — separated by a divider */}
                <div className="mt-4 border-t border-gray-100 pt-3">
                    <NavButton
                        tab={{ id: "assignments", label: "Assignments", icon: Tag }}
                        active={activeTab === "assignments"}
                        onClick={() => { actions.setTab("assignments"); onClose(); }}
                    />
                </div>
            </nav>

            {/* Footer */}
            <div className="border-t border-gray-100 px-4 py-3">
                <p className="text-[11px] font-medium text-gray-300">WPBean · v2.0.0</p>
            </div>
        </aside>
    );
}
